"use client";
import React, { useState, useRef, useEffect } from "react";

import clsx from "clsx";

import styles from "./Input.module.scss";

/**
 * Input component supports:
 * - input (default)
 * - textarea (multiline with auto-expand)
 * - custom select (styled dropdown with floating label)
 */
const Input = ({
  id,
  name,
  label,
  type = "text",
  value = "",
  placeholder = " ",
  required = false,
  disabled = false,
  error = "",
  helperText = "",
  icon: Icon,
  multiline = false,
  minRows = 2,
  maxRows,
  options = [],
  onChange,
  onBlur,
  onFocus,
  className = "",
  ...rest
}) => {
  const inputId = id || name;
  const hasOptions = Array.isArray(options) && options.length > 0;
  const [rows, setRows] = useState(minRows);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!hasOptions) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [hasOptions]);

  // Handle textarea auto-resize
  const handleChange = (e) => {
    if (multiline) {
      const lines = e.target.value.split("\n").length;
      setRows(Math.min(maxRows || Infinity, Math.max(minRows, lines)));
    }
    onChange && onChange(e);
  };

  // Handle select option click
  const handleOptionSelect = (opt) => {
    const evt = { target: { name, value: opt.value } };
    onChange && onChange(evt);
    setOpen(false);
  };

  // Determine display text for select
  const selected = options.find((o) => o.value === value);
  // Show selected label; if none selected and placeholder prop is non-empty, show placeholder; else show empty
  const displayText = selected ? selected.label : placeholder && placeholder.trim() ? placeholder.trim() : "";

  const commonProps = {
    id: inputId,
    name,
    value,
    placeholder,
    required,
    disabled,
    onBlur,
    onFocus,
    ...rest,
  };

  const hasIcon = Icon && !multiline && !hasOptions;
  return (
    <div
      ref={wrapperRef}
      className={clsx(
        styles.input,
        "Input__wrapper",
        {
          [styles.error]: !!error,
          [styles.disabled]: disabled,
          [styles.filled]: hasOptions && !!selected,
          [styles.iconMode]: hasIcon,
        },
        className
      )}
    >
      {/* icon only on simple input */}
      {hasIcon && (
        <span className={styles.iconWrapper} aria-hidden="true">
          <Icon />
        </span>
      )}

      {hasOptions ? (
        /* Select */
        <>
          <div className={clsx(styles.selectWrapper)} onClick={() => !disabled && setOpen((o) => !o)} tabIndex={0}>
            <span className={clsx(styles.selectedValue, !selected && styles.placeholder)}>{displayText}</span>
            <span className={styles.arrow} />
          </div>
          {open && (
            <ul className={styles.optionsList}>
              {options.map((opt) => (
                <li key={opt.value} onClick={() => handleOptionSelect(opt)}>
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : multiline ? (
        /* Textarea */
        <textarea {...commonProps} rows={rows} onChange={handleChange} />
      ) : (
        /* Input */
        <input {...commonProps} type={type} onChange={handleChange} />
      )}

      <label htmlFor={inputId}>{label}</label>
      {error && (
        <p id={`${inputId}-error`} className={styles.errorText} role="alert">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={`${inputId}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default Input;
